import React, { useState, useEffect } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import editIcon from "../assets/icons/edit.png";
import viewIcon from "../assets/icons/viewIcon.png";
import changeStatusIcon from "../assets/icons/changeStatusIcon.png";
import crossIcon from "../assets/icons/crossIcon.png";
import addIcon from "../assets/icons/addIcon.png";
import downloadIcon from "../assets/icons/downloadIcon.png";
import filterIcon from "../assets/icons/filterIcon.png";
import roleManagement from '../assets/icons/role-management.png';

import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";

const BASE_URL = `${process.env.REACT_APP_API_URL}/roles`;

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    role_name: "",
    role_description: "",
  });
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);

  const [roleStatsList, setRoleStatsList] = useState({
    totalRoles: 0,
    activeRoles: 0,
    inactiveRoles: 0,
  });

  const rolesStatsLists = [
    { label: "Total Roles", key: "totalRoles" },
    {
      label: "Active Roles",
      key: "activeRoles",
    },
    {
      label: "Inactive Roles",
      key: "inactiveRoles",
    },
  ];

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    console.log("Sorting by:", sortOrder);
    console.log("Filtering by day:", dayFilter);
  };

  const fetchRoles = async () => {
    if (!token) {
      showErrorToast(TOAST_MESSAGES.UNAUTHORIZED);
      return;
    }
    try {
      const response = await axios.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoles(response.data);
    } catch (error) {
      console.error("Failed to fetch roles", error);
      showErrorToast(TOAST_MESSAGES.ROLE.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const filteredRoles = roles
    .filter((role) => {
      const lower = searchTerm.toLowerCase();
      const roleName = role.role_name ? role.role_name.toLowerCase() : "";
      const roleDesc = role.role_description
        ? role.role_description.toLowerCase()
        : "";
      return roleName.includes(lower) || roleDesc.includes(lower);
    })
    .filter((role) => {
      if (!dayFilter) return true;
      const roleDate = new Date(role.created_timestamp);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return roleDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return roleDate >= yesterday && roleDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredRoles.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredRoles.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this role?")) return;
    try {
      await axios.delete(`${BASE_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchRoles();
      if (currentData.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (role) => {
    setFormData({
      role_name: role.role_name,
      role_description: role.role_description || "",
    });
    setEditingRoleId(role.role_id);
    setViewMode(false);
    setShowForm(true);
  };

  const handleView = (role) => {
    setFormData({
      role_name: role.role_name,
      role_description: role.role_description || "",
    });
    setEditingRoleId(role.role_id);
    setViewMode(true);
    setShowForm(true);
  };

const handleChangeStatus = async (role) => {
  const currentStatus = role.role_status; // Assuming "1" = Active, "0" = Inactive
  const newStatus = currentStatus === "1" ? "0" : "1";
  const statusText = newStatus === "1" ? "Active" : "Inactive";

  try {
    await axios.put(
      `${BASE_URL}/change-status/${role.role_id}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    fetchRoles();
    showSuccessToast(`${TOAST_MESSAGES.ROLE.STATUS_CHANGE_SUCCESS} to ${statusText}`);
  } catch (err) {
    console.error(err);
    showErrorToast(TOAST_MESSAGES.ROLE.STATUS_CHANGE_ERROR);
  }
};


  useEffect(() => {
    const calculateStats = () => {
      const totalRoles = roles.length;
      const activeRoles = roles.filter((r) => r.role_status === "1").length;
      const inactiveRoles = roles.filter((r) => r.role_status === "0").length;
      setRoleStatsList({
        totalRoles,
        activeRoles,
        inactiveRoles,
      });
    };
    calculateStats();
  }, [roles]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingRoleId ? `${BASE_URL}/${editingRoleId}` : BASE_URL;
      const method = editingRoleId ? "PUT" : "POST";
      await axios({
        method,
        url,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: formData,
      });
      setFormData({ role_name: "", role_description: "" });
      setShowForm(false);
      setEditingRoleId(null);
      fetchRoles();

      if (editingRoleId) {
        showSuccessToast(TOAST_MESSAGES.ROLE.UPDATE_SUCCESS);
      } else {
        showSuccessToast(TOAST_MESSAGES.ROLE.CREATE_SUCCESS);
      }
    } catch (err) {
      console.error(err);

      if (editingRoleId) {
        showErrorToast(TOAST_MESSAGES.ROLE.UPDATE_ERROR);
      } else {
        showErrorToast(TOAST_MESSAGES.ROLE.CREATE_ERROR);
      }
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setFormData({ role_name: "", role_description: "" });
    setEditingRoleId(null);
    setViewMode(false);
  };

  const exportExcel = () => {
    const wsData = [
      ["Role", "Description", "Status"],
      ...filteredRoles.map((r) => [
        r.role_name,
        r.role_description || "",
        r.role_status === 1 ? "Active" : "Inactive",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Roles");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "role_management.xlsx");
  };

  return (
    <div className="mx-2 pt-1  h-100">
      <div className="d-flex flex-wrap gap-3 mb-2 mt-0 justify-content-evenly align-items-center">
        {rolesStatsLists.map((item, index) => (
          <div key={index} className="dashboard-box p-3 rounded shadow-sm">
            <div className="d-flex align-items-center mb-2">
              <img
                src={roleManagement}
                alt={item.label}
                style={{
                  width: "22px",
                  height: "22px",
                  filter: "brightness(1) invert(1)",
                }}
              />
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>
            <h2 className="fw-bold">{roleStatsList[item.key]}</h2>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold" style={{ fontSize: "20px" }}>
          Role Management Details
        </span>
      </div>
      
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Roles"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn custom btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} alt="Download" />
          </button>

          <div className="dropdown">
            <button
              className="btn icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} alt="Filter" />
            </button>

            <div className="dropdown-menu p-2" style={{ minWidth: "200px" }}>
              <h6 className="fw-bolder text-black px-0">Time</h6>
              <hr className="mt-0" />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label
                    className="form-check-label notification-label"
                    htmlFor={order}
                  >
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr className="my-2" />
              <h6 className="fw-bolder text-black px-0">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label
                    className="form-check-label fw-5 notification-label"
                    htmlFor={day || "all"}
                  >
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr className="mt-1" />
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          {/* ✅ Replaced Bootstrap plus icon with custom addIcon image */}
          <button
            className="btn btn-outline-secondary action-button text-dark"
            onClick={() => setShowForm(true)}
            title="Add Role"
          >
            <img src={addIcon} alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted fs-5">No entries found</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table role-table table-sm w-100">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((r) => (
                  <tr key={r.role_id}>
                    <td>{r.role_name}</td>
                    <td title={r.role_description}>
                      {r.role_description || "—"}
                    </td>
                    <td>{r.role_status === "1" ? "Active" : "Inactive"}</td>
                    <td>{formatDateToUserTimeZone(r.updated_timestamp)}</td>
                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(r)}
                        title="View"
                      >
                        <img src={viewIcon} alt="View" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleEdit(r)}
                        title="Edit"
                      >
                        <img src={editIcon} alt="Edit" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleChangeStatus(r)}
                        title="Change Status"
                      >
                        <img src={changeStatusIcon} alt="Change Status" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="fixed-bottom-footer d-flex justify-content-between align-items-center mt-2 table-footer">
            <div className="text-muted">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-50 mx-auto">
              <form onSubmit={handleFormSubmit}>
                <div className="modal-header bg-white justify-content-center position-relative">
                  <h5 className="modal-title m-0">
                    {viewMode
                      ? "View Role"
                      : editingRoleId
                      ? "Update Role"
                      : "Add Role"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <img src={crossIcon} alt="Close" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.role_name}
                      onChange={(e) =>
                        setFormData({ ...formData, role_name: e.target.value })
                      }
                      required
                      disabled={viewMode}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={formData.role_description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          role_description: e.target.value,
                        })
                      }
                      disabled={viewMode}
                    />
                  </div>
                </div>
                <div className="modal-footer bg-white">
                  {!viewMode && (
                    <button
                      type="submit"
                      className="px-5 rounded text-white bg-dark border-none py-2 fw-bold"
                    >
                      {editingRoleId ? "Update Role" : "Add Role"}
                    </button>
                  )}
                  {viewMode && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
